import { getData, postData } from "@/request/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "@/lib/toast";

export const useGetProperties = (query: any) => {
  return useQuery({
    queryKey: ["properties", query],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (query.page) params.append("page", query.page);
      if (query.limit) params.append("limit", query.limit);
      if (query.type) params.append("type", query.type);
      if (query.location) params.append("location", query.location);
      if (query.price) params.append("price", query.price);
      if (query.area) params.append("area", query.area);
      if (query.tab) params.append("tab", query.tab);
      if (query.priceOrder) params.append("priceOrder", query.priceOrder);
      if (query.propertyId) params.append("propertyId", query.propertyId);
      if (query?.compareIds)
        params.append("compareIds", JSON.stringify(query.compareIds));
      if (query?.min) params.append("min", query.min);
      if (query?.max) params.append("max", query.max);

      const response = await getData(`properties?${params.toString()}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

export const useGetPropertyDetails = (slug: any) => {
  return useQuery({
    queryKey: ["property-details", slug],
    queryFn: async () => {
      const response = await getData(`properties/${slug}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

// GRT AGENTS
export const useGetAgents = (query: any) => {
  return useQuery({
    queryKey: ["agents", query],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query.page) params.append("page", query.page);
      if (query.limit) params.append("limit", query.limit);
      const response = await getData(`agents?${params.toString()}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

export const useGetAgent = (id: string) => {
  return useQuery({
    queryKey: ["agent", id],
    queryFn: async () => {
      const response = await getData(`agents/${id}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

// GET BANKS
export const useGetBanks = ({ page = 1, limit = 12 }: any) => {
  return useQuery({
    queryKey: ["banks", page, limit],
    queryFn: async () => {
      const response = await getData(`banks?page=${page}&limit=${limit}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    refetchInterval: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

export const useGetBank = (id: string) => {
  return useQuery({
    queryKey: ["bank", id],
    queryFn: async () => {
      const response = await getData(`banks/${id}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

// BLOGS HOOK
export const useGetBlogs = ({ page = 1, limit = 12 }) => {
  return useQuery({
    queryKey: ["blogs", page, limit],
    queryFn: async () => {
      const response = await getData(`blogs?page=${page}&limit=${limit}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

export const useGetBlog = (id: string) => {
  return useQuery({
    queryKey: ["blog", id],
    queryFn: async () => {
      const response = await getData(`blogs/${id}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

export const useGetDevelopers = (query: any) => {
  return useQuery({
    queryKey: ["developers", query],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (query.page) params.append("page", query.page);
      if (query.limit) params.append("limit", query.limit);
      const response = await getData(`developers?${params.toString()}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

export const useGetDeveloper = (id: string) => {
  return useQuery({
    queryKey: ["developer", id],
    queryFn: async () => {
      const response = await getData(`developer/${id}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

export const useGetAds = ({ page = 1, limit = 25 }) => {
  return useQuery({
    queryKey: ["ads", page, limit],
    queryFn: async () => {
      const response = await getData(`ads?page=${page}&limit=${limit}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

export const useGetAuctions = ({ page = 1, limit = 12, search = "" }) => {
  return useQuery({
    queryKey: ["auctions", page, limit, search],
    queryFn: async () => {
      const response = await getData(
        `actions?page=${page}&limit=${limit}&search=${search}`,
      );
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

export const useGetAuction = (slug: string) => {
  return useQuery({
    queryKey: ["auction", slug],
    queryFn: async () => {
      const response = await getData(`actions/${slug}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

// Developer UNITS
export const useGetDeveloperUnits = ({ slug, query }: any) => {
  return useQuery({
    queryKey: ["developerUnits", slug, query],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (query.page) params.append("page", query.page);
      if (query.limit) params.append("limit", query.limit);
      if (query.type) params.append("type", query.type);
      if (query.location) params.append("location", query.location);
      if (query.price) params.append("price", query.price);
      if (query.area) params.append("area", query.area);
      if (query.tab) params.append("tab", query.tab);
      if (query.priceOrder) params.append("priceOrder", query.priceOrder);
      if (query.propertyId) params.append("propertyId", query.propertyId);
      if (query?.compareIds)
        params.append("compareIds", JSON.stringify(query.compareIds));
      if (query?.min) params.append("min", query.min);
      if (query?.max) params.append("max", query.max);

      const response = await getData(
        `developer/units/${slug}?${params.toString()}`,
      );
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

export const useContactUs = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData(`contact`, data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res.message || "Message sent successfully");
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Failed to send message");
    },
  });
};

// REAL ESTATE PROJECTS
export const useGetProjects = ({
  page = 1,
  limit = 12,
  search = null,
}: any) => {
  return useQuery({
    queryKey: ["realEstateProjects", page, limit, search],
    queryFn: async () => {
      const response = await getData(
        `projects?page=${page}&limit=${limit}&search=${search}`,
      );
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

export const useGetProjectUnits = ({
  page = 1,
  limit = 12,
  slug,
  search = null,
}: any) => {
  return useQuery({
    queryKey: ["projectUnits", page, limit, slug, search],
    queryFn: async () => {
      const response = await getData(
        `projects/${slug}/units?page=${page}&limit=${limit}&search=${search}`,
      );
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};

export const useGetUnitDetails = (slug: string) => {
  return useQuery({
    queryKey: ["unitDetails", slug],
    queryFn: async () => {
      const response = await getData(`projects/unit/${slug}`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
    retry: 2,
  });
};
